import React, { useState, useEffect } from 'react';
import { X, Trophy, Heart } from 'lucide-react';
import { Product } from '../../types';

interface CopOrDropModalProps {
  isOpen: boolean;
  onClose: () => void;
  products: Product[];
  onCopVote: () => void;
  onDropVote: () => void;
  onComplete: () => void;
  currentItem: number;
  score: number;
}

export const CopOrDropModal: React.FC<CopOrDropModalProps> = ({
  isOpen,
  onClose,
  products,
  onCopVote,
  onDropVote,
  onComplete,
  currentItem,
  score
}) => {
  const [direction, setDirection] = useState<'left' | 'right' | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => setDirection(null), 300);
    return () => clearTimeout(timer);
  }, [currentItem]);

  const handleVote = (vote: 'cop' | 'drop') => {
    setDirection(vote === 'cop' ? 'right' : 'left');
    setTimeout(() => {
      if (vote === 'cop') onCopVote();
      else onDropVote();
    }, 200);
  };

  if (!isOpen) return null;

  // =========================================================================
  // 1. GAME OVER SCREEN
  // =========================================================================
  if (currentItem >= products.length || currentItem >= 10) {
    return (
      <div className="fixed inset-0 z-[200] bg-black flex overflow-hidden animate-in fade-in">
        
        {/* MOBILE GAME OVER (< 1024px) */}
        <div className="lg:hidden flex-1 flex flex-col items-center justify-center p-6">
          <div className="w-full max-w-sm text-center">
            <div className="w-16 h-16 border border-white/20 rounded-full flex items-center justify-center mx-auto mb-8">
              <Trophy size={32} className="text-white" />
            </div>
            <h2 className="text-3xl font-black text-white italic tracking-tighter uppercase mb-2">Session End</h2>
            <div className="text-8xl font-black text-white tracking-tighter mb-4">{score}</div>
            <p className="text-white/40 text-[10px] font-bold uppercase tracking-[0.3em] mb-12">Items copped to your bag</p>
            <div className="space-y-4">
              <button onClick={onComplete} className="w-full py-4 bg-white text-black font-black text-xs uppercase tracking-widest rounded-xl transition-transform active:scale-95">
                Review Selection
              </button>
              <button onClick={onClose} className="w-full py-4 text-white/40 font-bold text-[10px] uppercase tracking-widest hover:text-white">
                Exit to Home
              </button>
            </div>
          </div>
        </div>

        {/* DESKTOP GAME OVER (>= 1024px) */}
        <div className="hidden lg:flex w-full h-full">
           <div className="w-[60%] border-r border-white/20 bg-zinc-900 relative flex items-center justify-center overflow-hidden">
               <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '40px 40px' }} />
               <h1 className="text-[12rem] font-black italic tracking-tighter uppercase text-white/10 absolute rotate-[-10deg] scale-150 whitespace-nowrap">Session End</h1>
               <Trophy size={120} className="text-white relative z-10 animate-bounce" strokeWidth={1} />
           </div>
           <div className="w-[40%] bg-black p-16 flex flex-col justify-center text-center">
               <h2 className="text-4xl font-black text-white italic tracking-tighter uppercase mb-2">Shopping Spree Complete</h2>
               <p className="text-zinc-500 font-bold uppercase tracking-widest text-sm mb-12">Here is your haul.</p>
               <div className="text-[12rem] font-black text-white tracking-tighter leading-none mb-12">{score}</div>
               <button onClick={onComplete} className="w-full py-6 bg-white text-black font-black text-sm uppercase tracking-[0.2em] hover:bg-zinc-200 transition-colors mb-4">
                 Review Selection
               </button>
               <button onClick={onClose} className="w-full py-6 border border-white/20 text-zinc-500 font-bold text-xs uppercase tracking-widest hover:text-white hover:border-white transition-colors">
                 Exit to Home
               </button>
           </div>
        </div>

      </div>
    );
  }

  // =========================================================================
  // 2. ACTIVE GAME SCREEN
  // =========================================================================
  const product = products[currentItem];

  return (
    <div className="fixed inset-0 z-[200] bg-black flex overflow-hidden animate-in fade-in duration-300">
      
      {/* 📱 MOBILE LAYOUT (< 1024px) - EXACTLY AS ORIGINAL */}
      <div className="lg:hidden flex-1 flex flex-col h-full w-full relative">
        <div className="flex items-center justify-between p-6 z-10">
          <div className="bg-zinc-900 border border-white/10 px-4 py-2 rounded-full backdrop-blur-md">
              <span className="text-[10px] font-black text-white uppercase tracking-widest">{score} Copped</span>
          </div>
          <button onClick={onClose} className="w-10 h-10 bg-zinc-900 rounded-full flex items-center justify-center text-white border border-white/10">
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 flex flex-col items-center justify-center p-6 pt-0 relative overflow-hidden">
          <div 
            className={`relative w-full h-full max-h-[580px] rounded-[2.5rem] overflow-hidden border border-white/5 transition-transform duration-300 ease-out bg-zinc-900 ${
              direction === 'left' ? '-translate-x-[150%] rotate-[-15deg] opacity-0' : 
              direction === 'right' ? 'translate-x-[150%] rotate-[15deg] opacity-0' : 'translate-x-0 rotate-0'
            }`}
          >
            <img src={product.image} className="w-full h-full object-cover" alt={product.name} />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />

            <div className="absolute bottom-0 left-0 right-0 p-8">
               <div className="flex flex-col gap-1">
                  <span className="text-white/50 text-[10px] font-black uppercase tracking-[0.2em]">{product.brand}</span>
                  <h2 className="text-2xl font-black text-white italic tracking-tighter uppercase line-clamp-1">{product.name}</h2>
                  <span className="text-white font-bold text-sm mt-2">R {product.price}</span>
               </div>
            </div>

            {direction === 'right' && (
              <div className="absolute inset-0 bg-white/20 backdrop-blur-[2px] flex items-center justify-center">
                  <span className="text-white font-black text-5xl italic tracking-tighter uppercase border-4 border-white px-6 py-2">COP</span>
              </div>
            )}
            {direction === 'left' && (
              <div className="absolute inset-0 bg-black/60 backdrop-blur-[2px] flex items-center justify-center">
                  <span className="text-white/40 font-black text-5xl italic tracking-tighter uppercase border-4 border-white/20 px-6 py-2">DROP</span>
              </div>
            )}
          </div>
        </div>

        <div className="p-10 flex justify-center items-center gap-8 z-20">
          <button onClick={() => handleVote('drop')} className="w-16 h-16 rounded-full bg-zinc-900 border border-white/10 text-white flex items-center justify-center transition-all active:scale-90"><X size={28} strokeWidth={2} /></button>
          <button onClick={() => handleVote('cop')} className="w-20 h-20 rounded-full bg-white text-black flex items-center justify-center shadow-2xl active:scale-90 transition-all border-4 border-black"><Heart size={32} strokeWidth={3} className="fill-black" /></button>
        </div>

        <div className="h-1 w-full bg-zinc-900">
          <div className="h-full bg-white transition-all duration-300" style={{ width: `${(currentItem / 10) * 100}%` }} />
        </div>
      </div>

      {/* 🖥️ DESKTOP LAYOUT (>= 1024px) - SICKY MAG SPLIT */}
      <div className="hidden lg:flex w-full h-full">
         
         {/* LEFT: Massive Swipeable Image */}
         <div className="w-[65%] border-r border-white/20 bg-zinc-900 relative overflow-hidden flex items-center justify-center p-16">
            <button onClick={onClose} className="absolute top-8 left-8 p-3 bg-black/50 hover:bg-white hover:text-black text-white transition-colors border border-white/20 z-50">
              <X size={24} />
            </button>

            <div 
              className={`relative w-full h-full max-w-4xl mx-auto shadow-2xl transition-transform duration-300 ease-out ${
                direction === 'left' ? '-translate-x-[150%] rotate-[-5deg] opacity-0 scale-95' : 
                direction === 'right' ? 'translate-x-[150%] rotate-[5deg] opacity-0 scale-95' : 'translate-x-0 rotate-0 scale-100'
              }`}
            >
               <img src={product.image} className="w-full h-full object-cover" alt={product.name} />
               <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
               
               {/* Desktop Swipe Overlays */}
               {direction === 'right' && (
                  <div className="absolute inset-0 bg-white/20 backdrop-blur-sm flex items-center justify-center">
                     <span className="text-white font-black text-8xl italic tracking-tighter uppercase border-8 border-white px-12 py-4 shadow-2xl">COP</span>
                  </div>
               )}
               {direction === 'left' && (
                  <div className="absolute inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center">
                     <span className="text-white/40 font-black text-8xl italic tracking-tighter uppercase border-8 border-white/20 px-12 py-4">DROP</span>
                  </div>
               )}
            </div>
         </div>

         {/* RIGHT: Brutalist Control Panel */}
         <div className="w-[35%] bg-black flex flex-col p-12">
            
            {/* Header Info */}
            <div className="flex justify-between items-center border-b border-white/20 pb-6 mb-12">
               <h3 className="text-2xl font-black italic uppercase tracking-tighter">COP OR DROP</h3>
               <div className="text-right">
                  <span className="block text-3xl font-black font-mono leading-none">{score}</span>
                  <span className="text-[10px] text-zinc-500 font-bold uppercase tracking-widest">Copped</span>
               </div>
            </div>

            {/* Product Details */}
            <div className="mb-auto">
               <span className="text-zinc-500 text-sm font-bold uppercase tracking-[0.2em] block mb-4">{product.brand}</span>
               <h2 className="text-5xl font-black text-white italic tracking-tighter uppercase leading-[0.9] mb-6">{product.name}</h2>
               <div className="text-3xl font-mono text-white mb-8 border-l-4 border-white pl-4">R {product.price}</div>
               <p className="text-zinc-400 text-sm leading-relaxed">{product.description || 'Exclusive drop. Swipe or vote to add this item directly to your bag.'}</p>
            </div>

            {/* Big Desktop Buttons */}
            <div className="grid grid-cols-2 gap-4 mt-12 mb-8">
               <button 
                 onClick={() => handleVote('drop')}
                 className="py-8 border-2 border-white/20 text-white font-black text-xl uppercase tracking-widest hover:border-white hover:bg-white/5 transition-colors"
               >
                 Drop
               </button>
               <button 
                 onClick={() => handleVote('cop')}
                 className="py-8 bg-white text-black font-black text-xl uppercase tracking-widest hover:bg-zinc-200 transition-colors shadow-xl"
               >
                 Cop
               </button>
            </div>

            {/* Progress */}
            <div>
               <div className="flex justify-between text-xs font-bold text-zinc-500 uppercase tracking-widest mb-3">
                  <span>Item {currentItem + 1}</span>
                  <span>10 Max</span>
               </div>
               <div className="h-1 w-full bg-zinc-900">
                  <div className="h-full bg-white transition-all duration-300" style={{ width: `${((currentItem) / 10) * 100}%` }} />
               </div>
            </div>

         </div>
      </div>

    </div>
  );
};